import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '@/components/Layout';
import { formatRego, formatAUMobile } from '@/lib/formatters';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  Search, 
  Car, 
  User, 
  Phone, 
  Mail,
  Calendar,
  CheckCircle,
  ClipboardList,
  Key,
  QrCode,
  Smartphone,
  Upload,
  Camera
} from 'lucide-react';
import { toast } from 'sonner';
import { useSite } from '@/context/SiteContext';
import debounce from 'lodash.debounce';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SERVICE_TYPES = [
  { value: 'service', label: 'Service', color: 'bg-blue-100 text-blue-700' },
  { value: 'rwc', label: 'RWC (Roadworthy)', color: 'bg-green-100 text-green-700' },
  { value: 'tyres', label: 'Tyres', color: 'bg-purple-100 text-purple-700' },
  { value: 'aircon', label: 'Aircon', color: 'bg-cyan-100 text-cyan-700' },
  { value: 'brakes', label: 'Brakes', color: 'bg-orange-100 text-orange-700' },
  { value: 'suspension', label: 'Suspension', color: 'bg-yellow-100 text-yellow-700' },
  { value: 'electrical', label: 'Electrical', color: 'bg-indigo-100 text-indigo-700' },
  { value: 'other', label: 'Other', color: 'bg-gray-100 text-gray-700' }
];

export default function KioskCheckIn() {
  const navigate = useNavigate();
  const { getSiteParams } = useSite();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  
  // Service types - now supports multiple selection
  const [selectedServiceTypes, setSelectedServiceTypes] = useState([]);
  
  const [formData, setFormData] = useState({
    comments: '',
    customer_id: '',
    customer_name: '',
    customer_phone: '',
    customer_email: '',
    vehicle_id: '',
    vehicle_rego: '',
    vehicle_make: '',
    vehicle_model: '',
    loan_car_requested: false,
    loan_car_rego: '',
    customer_license_number: '',
    loan_car_pickup_datetime: '',
    loan_car_dropoff_datetime: '',
    terms_accepted: false,
    scheduled_date: '' // Date the customer wants to book for
  });
  
  // QR Code state for loan car
  const [loanCarQrCode, setLoanCarQrCode] = useState(null);
  const [qrLoading, setQrLoading] = useState(false);
  const [customerSubmission, setCustomerSubmission] = useState(null);

  // Generate QR code when loan car rego changes
  const generateLoanCarQR = async (rego) => {
    if (!rego || rego.length < 3) {
      setLoanCarQrCode(null);
      return;
    }
    
    setQrLoading(true);
    try {
      // First create or get the loan car record
      const response = await fetch(`${BACKEND_URL}/api/loan-cars/generate-kiosk-qr`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ 
          loan_car_rego: rego,
          customer_name: formData.customer_name || 'Walk-in Customer',
          customer_phone: formData.customer_phone || ''
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setLoanCarQrCode(data);
      }
    } catch (error) {
      console.error('Failed to generate QR code:', error);
    } finally {
      setQrLoading(false);
    }
  };

  useEffect(() => {
    // QR generation is no longer triggered from the kiosk — the loan car
    // rego is picked from the Loan Cars page after check-in. Keep the QR
    // state cleared so any stale image is removed when the checkbox flips.
    setLoanCarQrCode(null);
  }, [formData.loan_car_requested]);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/auth/me`, {
          credentials: 'include'
        });
        if (response.ok) {
          const userData = await response.json();
          setUser(userData);
        } else {
          navigate('/login');
        }
      } catch (error) {
        console.error('Failed to fetch user:', error);
        navigate('/login');
      } finally {
        setLoading(false);
      }
    };
    fetchUser();
  }, [navigate]);

  // Debounced search function
  const debouncedSearch = useCallback(
    debounce(async (query) => {
      if (!query || query.length < 2) {
        setSearchResults([]);
        return;
      }

      setSearching(true);
      try {
        const siteParams = getSiteParams();
        const sep = siteParams ? '&' : '?';
        const response = await fetch(
          `${BACKEND_URL}/api/bookings/search/customers${siteParams}${sep}query=${encodeURIComponent(query)}`,
          { credentials: 'include' }
        );

        if (response.ok) {
          const data = await response.json();
          setSearchResults(data.results || []);
        }
      } catch (error) {
        console.error('Search failed:', error);
      } finally {
        setSearching(false);
      }
    }, 300),
    [getSiteParams]
  );

  useEffect(() => {
    debouncedSearch(searchQuery);
    return () => debouncedSearch.cancel();
  }, [searchQuery, debouncedSearch]);

  const handleSelectCustomer = (customer) => {
    setSelectedCustomer(customer);
    setFormData(prev => ({
      ...prev,
      customer_id: customer.customer_id || '',
      customer_name: customer.display_name || '',
      customer_phone: customer.phone || '',
      customer_email: customer.email || ''
    }));
    setSearchQuery('');
    setSearchResults([]);
  };

  const handleSelectVehicle = (vehicle) => {
    setSelectedVehicle(vehicle);
    setFormData(prev => ({
      ...prev,
      vehicle_id: vehicle.vehicle_id || '',
      vehicle_rego: vehicle.rego || '',
      vehicle_make: vehicle.make || '',
      vehicle_model: vehicle.model || ''
    }));
  };

  // Handle service type toggle for multi-select
  const handleServiceTypeToggle = useCallback((type) => {
    setSelectedServiceTypes(prev => {
      if (prev.includes(type)) {
        return prev.filter(t => t !== type);
      } else {
        return [...prev, type];
      }
    });
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (selectedServiceTypes.length === 0) {
      toast.error('Please select at least one service type');
      return;
    }

    setSubmitting(true);
    try {
      const siteParams = getSiteParams();
      
      // Parse scheduled date if provided, otherwise use current datetime
      let bookedDatetime = null;
      if (formData.scheduled_date) {
        // Send the date with time as a plain ISO string (no timezone conversion)
        // This ensures the date stays as selected by the user
        bookedDatetime = `${formData.scheduled_date}T09:00:00`;
      }
      
      const payload = {
        service_types: selectedServiceTypes,  // Send array of service types
        comments: formData.comments || null,
        customer_id: formData.customer_id || null,
        customer_name: formData.customer_name || null,
        customer_phone: formData.customer_phone || null,
        customer_email: formData.customer_email || null,
        vehicle_id: formData.vehicle_id || null,
        vehicle_rego: formData.vehicle_rego || null,
        vehicle_make: formData.vehicle_make || null,
        vehicle_model: formData.vehicle_model || null,
        loan_car_requested: formData.loan_car_requested,
        loan_car_rego: formData.loan_car_rego || null,
        customer_license_number: formData.customer_license_number || null,
        loan_car_pickup_datetime: formData.loan_car_pickup_datetime || null,
        loan_car_dropoff_datetime: formData.loan_car_dropoff_datetime || null,
        terms_accepted: formData.terms_accepted,
        booked_datetime: bookedDatetime
      };

      const response = await fetch(`${BACKEND_URL}/api/bookings${siteParams}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        const booking = await response.json();
        const dateMsg = formData.scheduled_date 
          ? ` for ${new Date(formData.scheduled_date + 'T00:00:00').toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' })}`
          : '';
        const servicesText = selectedServiceTypes.length > 1 
          ? ` (${selectedServiceTypes.map(t => SERVICE_TYPES.find(s => s.value === t)?.label || t).join(', ')})`
          : '';
        toast.success(`Booking ${booking.booking_number} created successfully${dateMsg}${servicesText}!`);
        navigate('/bookings');
      } else {
        const errorData = await response.json().catch(() => ({ detail: 'Failed to create booking' }));
        toast.error(errorData.detail || 'Failed to create booking');
      }
    } catch (error) {
      console.error('Failed to create booking:', error);
      toast.error('Failed to create booking');
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setSelectedCustomer(null);
    setSelectedVehicle(null);
    setSelectedServiceTypes([]);
    setFormData({
      comments: '',
      customer_id: '',
      customer_name: '',
      customer_phone: '',
      customer_email: '',
      vehicle_id: '',
      vehicle_rego: '',
      vehicle_make: '',
      vehicle_model: '',
      loan_car_requested: false,
      loan_car_rego: '',
      customer_license_number: '',
      loan_car_pickup_datetime: '',
      loan_car_dropoff_datetime: '',
      terms_accepted: false,
      scheduled_date: ''
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent"></div>
          <p className="mt-4 text-slate-600 font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <Layout user={user}>
      <div className="p-4 md:p-6 lg:p-8 max-w-4xl mx-auto" data-testid="kiosk-checkin-container">
        {/* Header */}
        <div className="mb-8 text-center">
          <h1 className="font-['Chivo'] font-bold text-4xl sm:text-5xl text-slate-900 mb-2">
            New Booking
          </h1>
          <p className="text-slate-500 text-lg">
            Create a new service booking
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Customer Search */}
          <Card className="bg-white border border-slate-200 rounded-lg shadow-sm p-6">
            <h2 className="font-semibold text-lg text-slate-900 mb-4 flex items-center gap-2">
              <Search className="h-5 w-5 text-blue-600" />
              Search Existing Customer
            </h2>
            
            <div className="relative">
              <Input
                placeholder="Search by name, phone, email, or vehicle rego..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="h-12"
                data-testid="customer-search-input"
              />
              {searching && (
                <div className="absolute right-3 top-3">
                  <div className="animate-spin h-6 w-6 border-2 border-blue-600 border-t-transparent rounded-full"></div>
                </div>
              )}
              
              {searchResults.length > 0 && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-64 overflow-y-auto">
                  {searchResults.map((customer) => (
                    <div
                      key={customer.customer_id}
                      className="p-3 hover:bg-slate-50 cursor-pointer border-b last:border-b-0"
                      onClick={() => handleSelectCustomer(customer)}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-slate-900">{customer.display_name}</p>
                          <p className="text-sm text-slate-500">
                            {customer.phone && <span className="mr-3">{customer.phone}</span>}
                            {customer.email && <span>{customer.email}</span>}
                          </p>
                        </div>
                        {customer.vehicles?.length > 0 && (
                          <div className="text-right">
                            <p className="text-xs text-slate-500">{customer.vehicles.length} vehicle(s)</p>
                            <p className="text-sm font-medium text-slate-700">
                              {customer.vehicles[0].rego}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {selectedCustomer && (
              <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                    <span className="font-medium text-green-800">Customer Selected: {selectedCustomer.display_name}</span>
                  </div>
                  <Button type="button" variant="ghost" size="sm" onClick={() => {
                    setSelectedCustomer(null);
                    setFormData(prev => ({ ...prev, customer_name: '', customer_phone: '', customer_email: '' }));
                  }}>
                    Clear
                  </Button>
                </div>
                
                {selectedCustomer.vehicles?.length > 0 && (
                  <div className="mt-3">
                    <p className="text-sm text-green-700 mb-2">Select a vehicle:</p>
                    <div className="flex flex-wrap gap-2">
                      {selectedCustomer.vehicles.map((vehicle) => (
                        <Button
                          key={vehicle.vehicle_id}
                          type="button"
                          variant={selectedVehicle?.vehicle_id === vehicle.vehicle_id ? "default" : "outline"}
                          size="sm"
                          onClick={() => handleSelectVehicle(vehicle)}
                          className={selectedVehicle?.vehicle_id === vehicle.vehicle_id ? "bg-green-600 hover:bg-green-700" : ""}
                        >
                          <Car className="h-4 w-4 mr-1" />
                          {vehicle.rego} {vehicle.make && `(${vehicle.make})`}
                        </Button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </Card>

          {/* Customer Details */}
          <Card className="bg-white border border-slate-200 rounded-lg shadow-sm p-6">
            <h2 className="font-semibold text-lg text-slate-900 mb-4 flex items-center gap-2">
              <User className="h-5 w-5 text-blue-600" />
              Customer Details <span className="text-sm font-normal text-slate-500">(Optional)</span>
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="customer_name">Full Name</Label>
                <Input
                  id="customer_name"
                  value={formData.customer_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, customer_name: e.target.value }))}
                  placeholder="John Smith"
                  className="h-12 mt-1"
                  data-testid="customer-name-input"
                />
              </div>
              <div>
                <Label htmlFor="customer_phone">Phone Number</Label>
                <Input
                  id="customer_phone"
                  value={formData.customer_phone}
                  onChange={(e) => setFormData(prev => ({ ...prev, customer_phone: formatAUMobile(e.target.value) }))}
                  placeholder="0400 000 000"
                  className="h-12 mt-1"
                  data-testid="customer-phone-input"
                />
              </div>
              <div>
                <Label htmlFor="customer_email">Email</Label>
                <Input
                  id="customer_email"
                  type="email"
                  value={formData.customer_email}
                  onChange={(e) => setFormData(prev => ({ ...prev, customer_email: e.target.value }))}
                  placeholder="john@example.com"
                  className="h-12 mt-1"
                  data-testid="customer-email-input"
                />
              </div>
            </div>
          </Card>

          {/* Vehicle Details */}
          <Card className="bg-white border border-slate-200 rounded-lg shadow-sm p-6">
            <h2 className="font-semibold text-lg text-slate-900 mb-4 flex items-center gap-2">
              <Car className="h-5 w-5 text-blue-600" />
              Vehicle Details <span className="text-sm font-normal text-slate-500">(Optional)</span>
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="vehicle_rego">Registration (Rego)</Label>
                <Input
                  id="vehicle_rego"
                  value={formData.vehicle_rego}
                  onChange={(e) => setFormData(prev => ({ ...prev, vehicle_rego: formatRego(e.target.value) }))}
                  placeholder="ABC123"
                  className="h-12 mt-1"
                  data-testid="vehicle-rego-input"
                />
              </div>
              <div>
                <Label htmlFor="vehicle_make">Make</Label>
                <Input
                  id="vehicle_make"
                  value={formData.vehicle_make}
                  onChange={(e) => setFormData(prev => ({ ...prev, vehicle_make: e.target.value }))}
                  placeholder="Toyota"
                  className="h-12 mt-1"
                  data-testid="vehicle-make-input"
                />
              </div>
              <div>
                <Label htmlFor="vehicle_model">Model</Label>
                <Input
                  id="vehicle_model"
                  value={formData.vehicle_model}
                  onChange={(e) => setFormData(prev => ({ ...prev, vehicle_model: e.target.value }))}
                  placeholder="Camry"
                  className="h-12 mt-1"
                  data-testid="vehicle-model-input"
                />
              </div>
            </div>
          </Card>

          {/* Loan Car Section */}
          <Card className="bg-white border border-slate-200 rounded-lg shadow-sm p-6">
            <div className="flex items-start gap-3">
              <Checkbox
                id="loan_car_requested"
                checked={formData.loan_car_requested}
                onCheckedChange={(checked) => setFormData(prev => ({
                  ...prev,
                  loan_car_requested: checked,
                  // Clear any previously-entered loan-car details so they
                  // don't tag along if the SM rechecks then unchecks.
                  loan_car_rego: '',
                  customer_license_number: '',
                  loan_car_pickup_datetime: '',
                  loan_car_dropoff_datetime: '',
                }))}
                data-testid="loan-car-checkbox"
              />
              <Label htmlFor="loan_car_requested" className="cursor-pointer flex-1">
                <span className="font-semibold text-lg text-slate-900 flex items-center gap-2">
                  <Key className="h-5 w-5 text-blue-600" />
                  Customer Picking Up Loan Car
                </span>
                <span className="block text-xs text-slate-500 mt-1">
                  Ticking this just earmarks a loan car for the day —
                  rego, pickup time, and license capture are handled
                  later from the Loan Cars page.
                </span>
              </Label>
            </div>
          </Card>

          {/* Service Type Selection - Multi-select.
              Moved below the Loan Car Section so the SM finishes the
              customer/vehicle/loan-car logistics first, then captures the
              services to perform on the vehicle as the final step before
              the Terms acceptance. */}
          <Card className="bg-white border border-slate-200 rounded-lg shadow-sm p-6">
            <h2 className="font-semibold text-lg text-slate-900 mb-2 flex items-center gap-2">
              <ClipboardList className="h-5 w-5 text-blue-600" />
              Service Types <span className="text-red-500">*</span>
            </h2>
            <p className="text-sm text-slate-500 mb-4">Select all services the customer needs</p>
            
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {SERVICE_TYPES.map((type) => {
                const isSelected = selectedServiceTypes.includes(type.value);
                return (
                  <label
                    key={type.value}
                    className={`relative flex items-center gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all ${
                      isSelected
                        ? "border-blue-500 bg-blue-50"
                        : "border-slate-200 hover:border-slate-300 hover:bg-slate-50"
                    }`}
                    data-testid={`service-type-${type.value}`}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => handleServiceTypeToggle(type.value)}
                      className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className={`font-medium ${isSelected ? "text-blue-700" : "text-slate-700"}`}>
                      {type.label}
                    </span>
                  </label>
                );
              })}
            </div>
            
            {/* Selected services summary */}
            {selectedServiceTypes.length > 0 && (
              <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                <p className="text-sm font-medium text-blue-700">
                  Selected: {selectedServiceTypes.map(t => SERVICE_TYPES.find(s => s.value === t)?.label || t).join(', ')}
                </p>
              </div>
            )}
            
            {selectedServiceTypes.length === 0 && (
              <p className="mt-3 text-sm text-red-500">Please select at least one service type</p>
            )}

            {/* Scheduled Date */}
            <div className="mt-6 pt-6 border-t border-slate-200">
              <div className="flex items-center gap-2 mb-3">
                <Calendar className="h-5 w-5 text-blue-600" />
                <Label htmlFor="scheduled_date" className="font-semibold text-slate-900">
                  Scheduled Date
                </Label>
                <span className="text-sm text-slate-500">(Leave empty for today)</span>
              </div>
              <Input
                id="scheduled_date"
                type="date"
                value={formData.scheduled_date}
                onChange={(e) => setFormData(prev => ({ ...prev, scheduled_date: e.target.value }))}
                min={new Date().toISOString().split('T')[0]}
                className="h-12 max-w-xs"
                data-testid="scheduled-date-input"
              />
              {formData.scheduled_date && (
                <p className="mt-2 text-sm text-blue-600 flex items-center gap-1">
                  <Calendar className="h-4 w-4" />
                  Booking scheduled for: {new Date(formData.scheduled_date + 'T00:00:00').toLocaleDateString('en-AU', { 
                    weekday: 'long', 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}
                </p>
              )}
              {!formData.scheduled_date && (
                <p className="mt-2 text-sm text-slate-500">
                  No date selected - booking will be created for today
                </p>
              )}
            </div>

            <div className="mt-4">
              <Label htmlFor="comments">Comments / Notes</Label>
              <Textarea
                id="comments"
                value={formData.comments}
                onChange={(e) => setFormData(prev => ({ ...prev, comments: e.target.value }))}
                placeholder="Any additional details about the service required..."
                rows={3}
                className="mt-1"
                data-testid="comments-input"
              />
            </div>
          </Card>

          {/* Terms & Submit */}
          <Card className="bg-white border border-slate-200 rounded-lg shadow-sm p-6">
            <div className="flex items-start gap-3 mb-6">
              <Checkbox
                id="terms_accepted"
                checked={formData.terms_accepted}
                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, terms_accepted: checked }))}
                className="mt-1"
                data-testid="terms-checkbox"
              />
              <Label htmlFor="terms_accepted" className="text-slate-700 cursor-pointer">
                I accept the terms and conditions. The customer acknowledges that the workshop is not liable for any items left in the vehicle during service.
              </Label>
            </div>

            <div className="flex gap-4">
              <Button
                type="submit"
                disabled={submitting || selectedServiceTypes.length === 0}
                className="flex-1 h-14 text-lg bg-blue-600 hover:bg-blue-700"
                data-testid="submit-booking-button"
              >
                {submitting ? (
                  <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full"></div>
                ) : (
                  <>
                    <CheckCircle className="h-5 w-5 mr-2" />
                    Create Booking
                  </>
                )}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={resetForm}
                className="h-14 px-8"
                data-testid="reset-form-button"
              >
                Reset
              </Button>
            </div>
          </Card>
        </form>
      </div>
    </Layout>
  );
}
